interface DeepSeekResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
}

interface AnalysisResult {
  strengths: string[];
  weaknesses: string[];
  learningStyle: string;
  cognitiveProfile: string;
  detailedInsights: string[];
  recommendations: string[];
  nextSteps: string[];
}

export class DeepSeekService {
  private apiKey: string;
  private baseUrl: string = 'https://api.deepseek.com/v1/chat/completions';

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async analyzeUserPerformance(
    responses: any[], 
    questions: any[], 
    subject: string
  ): Promise<AnalysisResult> {
    // 构建详细的分析prompt
    const analysisPrompt = this.buildAnalysisPrompt(responses, questions, subject);
    
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `你是一个专业的教育心理学专家和学习分析师。你需要基于学生的答题数据，提供深度的学习能力分析和个性化建议。

要求：
1. 分析要科学严谨，基于认知心理学理论
2. 建议要具体可操作，不要空泛
3. 返回标准JSON格式
4. 用中文回答`
            },
            {
              role: 'user',
              content: analysisPrompt
            }
          ],
          temperature: 0.7,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`DeepSeek API error: ${response.status}`);
      }

      const data: DeepSeekResponse = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('Empty response from DeepSeek API');
      }

      return this.parseAnalysisResult(content);
    } catch (error) {
      console.error('DeepSeek API调用失败:', error);
      // 返回fallback分析结果
      return this.getFallbackAnalysis();
    }
  }

  private buildAnalysisPrompt(responses: any[], questions: any[], subject: string): string {
    const performanceData = responses.map((response, index) => {
      const question = questions[index];
      return {
        题目ID: question.id,
        学科: subject === 'math' ? '数学' : '语文',
        认知维度: this.getDimensionName(question.skill_dimension),
        难度等级: question.difficulty,
        题目类型: question.question_type,
        学生答案: response.answer,
        正确答案: question.content.correct_answer,
        是否正确: response.is_correct,
        答题时间: Math.round(response.time_spent / 1000) + '秒',
        题目限时: question.time_limit ? question.time_limit + '秒' : '无限制',
        题目内容: question.content.title
      };
    });

    return `请分析以下学生的答题表现数据：

学科：${subject === 'math' ? '数学' : '语文'}
答题数据：
${JSON.stringify(performanceData, null, 2)}

请从以下维度进行深度分析：

1. **优势能力识别**：学生表现突出的认知能力和学习特长
2. **薄弱环节分析**：需要重点改进的能力维度和具体问题
3. **学习风格判断**：基于答题时间和准确率分析学习偏好
4. **认知特征画像**：总结学生的思维特点和认知模式
5. **深度洞察**：发现数据背后的学习规律和潜在问题
6. **个性化建议**：针对性的学习方法和练习建议
7. **下一步行动**：具体的学习计划和改进措施

请以JSON格式返回分析结果：
{
  "strengths": ["优势1", "优势2", ...],
  "weaknesses": ["薄弱点1", "薄弱点2", ...],
  "learningStyle": "学习风格描述",
  "cognitiveProfile": "认知特征总结",
  "detailedInsights": ["洞察1", "洞察2", ...],
  "recommendations": ["建议1", "建议2", ...],
  "nextSteps": ["行动1", "行动2", ...]
}`;
  }

  private getDimensionName(dimension: string): string {
    const names: { [key: string]: string } = {
      processing_speed: '信息处理速度',
      working_memory: '工作记忆',
      abstract_thinking: '抽象思维',
      pattern_recognition: '模式识别',
      transfer_application: '迁移应用',
      metacognitive_monitoring: '元认知监控'
    };
    return names[dimension] || dimension;
  }

  private parseAnalysisResult(content: string): AnalysisResult {
    try {
      // 尝试解析JSON
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        return {
          strengths: result.strengths || [],
          weaknesses: result.weaknesses || [],
          learningStyle: result.learningStyle || '',
          cognitiveProfile: result.cognitiveProfile || '',
          detailedInsights: result.detailedInsights || [],
          recommendations: result.recommendations || [],
          nextSteps: result.nextSteps || []
        };
      }
    } catch (error) {
      console.error('解析AI分析结果失败:', error);
    }
    
    return this.getFallbackAnalysis();
  }

  private getFallbackAnalysis(): AnalysisResult {
    return {
      strengths: ['基础计算能力较好', '学习态度认真'],
      weaknesses: ['需要提高答题速度', '逻辑推理有待加强'],
      learningStyle: '稳健型学习者，注重准确性胜过速度',
      cognitiveProfile: '思维较为细致，但需要提高思维敏捷性',
      detailedInsights: [
        '答题时间相对较长，说明思考过程比较谨慎',
        '在复杂题目上表现有提升空间'
      ],
      recommendations: [
        '建议增加限时练习来提高答题速度',
        '多做逻辑推理类题目来强化思维能力'
      ],
      nextSteps: [
        '每天进行15分钟的限时练习',
        '重点练习逻辑推理和模式识别类题目'
      ]
    };
  }

  async generateTargetedQuestions(
    weaknesses: string[],
    subject: string,
    count: number = 3
  ): Promise<any[]> {
    const prompt = `基于学生的薄弱环节，为${subject === 'math' ? '数学' : '语文'}学科生成${count}道针对性练习题。

薄弱环节：${weaknesses.join(', ')}

要求：
1. 题目要有针对性，直击薄弱环节
2. 难度适中，既有挑战性又不会过于困难
3. 包含选择题格式，提供4个选项
4. 每题包含详细解析
5. 返回JSON数组格式
6. **重要：选项内容不要包含A.、B.、C.、D.等前缀标签，只提供纯选项内容**

返回格式：
[
  {
    "title": "题目内容",
    "options": ["选项内容1", "选项内容2", "选项内容3", "选项内容4"],
    "correct_answer": "正确答案内容",
    "explanation": "详细解析",
    "target_weakness": "针对的薄弱环节"
  }
]

注意：选项数组中每个元素应该只包含选项的具体内容，不要包含"A."、"B."等标签前缀。`;

    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: '你是一个资深的教育专家，擅长根据学生的学习薄弱环节设计针对性的练习题目。请确保生成的选项格式正确，不包含A.B.C.D.等前缀。'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.8,
          max_tokens: 1500
        })
      });

      const data: DeepSeekResponse = await response.json();
      const content = data.choices[0]?.message?.content;

      if (content) {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          const questions = JSON.parse(jsonMatch[0]);
          // 清理可能存在的前缀标签
          return questions.map((q: any) => ({
            ...q,
            options: q.options.map((option: string) => 
              // 移除可能的A.、B.、C.、D.前缀
              option.replace(/^[A-D]\.\s*/, '').trim()
            )
          }));
        }
      }
    } catch (error) {
      console.error('生成题目失败:', error);
    }

    // 返回fallback题目
    return this.getFallbackQuestions(subject);
  }

  private getFallbackQuestions(subject: string): any[] {
    if (subject === 'math') {
      return [
        {
          title: "计算：45 × 12 = ?",
          options: ["520", "540", "560", "580"],
          correct_answer: "540",
          explanation: "45 × 12 = 45 × (10 + 2) = 450 + 90 = 540",
          target_weakness: "计算速度"
        }
      ];
    } else {
      return [
        {
          title: "下列成语使用正确的是？",
          options: [
            "他对这件事漠不关心", 
            "这个问题让人深思熟虑", 
            "我们要防微杜渐", 
            "他说话总是言简意赅"
          ],
          correct_answer: "他说话总是言简意赅",
          explanation: "言简意赅指说话简洁明了，用法正确",
          target_weakness: "语言理解"
        }
      ];
    }
  }
}